// oxlint-disable no-unescaped-entities
// oxlint-disable no-unused-vars
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useCallback } from "react";
import { Importer, ImporterField } from "react-csv-importer";
import { toast } from "react-toastify";
import { CSVImportRows } from "../../../../utilities/types";
import ImportTableCSV from "./ImportTableCSV";
import { useDropzone } from "react-dropzone";
import { styled } from "@mui/material/styles";
import { read, utils, WorkSheet } from "xlsx";
import { Download, Launch, NotInterested } from "@mui/icons-material";
import { FileTaggingAndSplitting } from "./FileTaggingAndSplitting";
import { ImportFormProps } from "../../shared/ImportHelpers";
import { CustomAutocomplete, DealSelectionAutocomplete } from "../../../helpers";
import { useImportFormCSVViewModel } from "./ImportForm_CSV.vm";

const fgColor = "#000";
const fillColor = "#f0f0f0";
const borderRadius = "0.4em";
const textColor = "#202020";

export function flattenArrayObjects<T extends string | number | symbol, U>(records: Record<T, U[]>[]): Record<T, U[]> {
	// TODO should do this better...
	if (records.length === 0) return {} as Record<T, U[]>;

	const result: Record<T, U[]> = { ...records[0] };
	// reset all keys of result
	for (const key of Object.keys(result)) {
		// @ts-ignore
		result[key] = [];
	}
	for (const record of records) {
		for (const [key, value] of Object.entries(record)) {
			// @ts-ignore
			if (!result[key]) {
				// @ts-ignore
				result[key] = [];
			}
			// @ts-ignore
			result[key] = result[key].concat(value);
		}
	}
	return result;
}

function darken(color: string, percentage: number) {
	const num = parseInt(color.replace("#", ""), 16),
		amt = Math.round(2.55 * percentage),
		R = (num >> 16) - amt,
		B = ((num >> 8) & 0x00ff) - amt,
		G = (num & 0x0000ff) - amt;
	return (
		"#" +
		(
			0x1000000 +
			(R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
			(B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
			(G < 255 ? (G < 1 ? 0 : G) : 255)
		)
			.toString(16)
			.slice(1)
	);
}

const CSVImporter_FileSelector = styled("div")(({ theme }) => ({
	border: `0.25em dashed ${fgColor}`,
	padding: "4em",
	borderRadius: borderRadius,
	background: fillColor,
	textAlign: "center",
	color: textColor,
	cursor: "pointer",
	'&[data-active="true"]': {
		background: darken(fillColor, 10),
		transition: "background 0.1s ease-out",
	},
}));

export function CSVImportForm(props: ImportFormProps) {
	const vm = useImportFormCSVViewModel(props);

	function CSVDropzone() {
		const onDrop = useCallback(async (acceptedFiles: File[]) => {
			for (const file of acceptedFiles) {
				const wb = read(await file.arrayBuffer());

				let selectedSheet: WorkSheet | null = null;
				let seletedSheetName: string | null = null;
				for (const sheetName of wb.SheetNames) {
					if (wb.SheetNames.length === 1 || sheetName.toLowerCase().includes("csv download tab")) {
						selectedSheet = wb.Sheets[sheetName];
						seletedSheetName = sheetName;
						break;
					}
				}

				if (!selectedSheet) {
					toast.error("Unable to determine correct sheet");
					return;
				}

				const rows = utils.sheet_to_json(selectedSheet);
			}
		}, []);
		const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

		return (
			<CSVImporter_FileSelector {...getRootProps()}>
				<input {...getInputProps()} />
				{isDragActive ? <p>Drop the files here ...</p> : <p>Drag 'n' drop some files here, or click to select files</p>}
			</CSVImporter_FileSelector>
		);
	}

	return (
		<>
			<Grid
				container
				spacing={1}
				sx={{ alignItems: "stretch", alignContent: "stretch", maxWidth: "md", margin: "auto", width: "100%" }}
			>
				{/* Internal Branch/Deal Selection */}
				<Grid size={12}>
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", md: "row" },
							gap: 2,
							width: "100%",
							pt: 1,
						}}
					>
						{vm.canShowBranchSelector && (
							<CustomAutocomplete
								optionLabelField="name"
								loading={vm.loading}
								options={props.branches || []}
								selectedOption={vm.internalSelectedBranch}
								onChange={vm.handleInternalBranchChange}
								optionFormatter={(option) => option.name}
								label="Choose your branch"
								sx={{ flex: 1 }}
							/>
						)}
						<DealSelectionAutocomplete
							deals={props.deals}
							selectedDeals={vm.internalSelectedDeal ? [vm.internalSelectedDeal] : []}
							loading={vm.loading}
							setSelectedDeals={(deals) => vm.handleInternalDealChange(deals[0] ?? null)}
							sx={{ flex: 1 }}
						/>
					</Box>
				</Grid>

				{vm.canShowImporter && (
					<>
						{vm.loading && (
							<Grid size={12}>
								<Typography>Loading...</Typography>
							</Grid>
						)}
						<Grid size={12} sx={{ width: "100%" }}>
							{vm.csvImportType === "3rdParty" && (
								<Importer<CSVImportRows>
									skipEmptyLines={false}
									dataHandler={vm.importCSV}
									defaultNoHeader={false}
									restartable={true}
									chunkSize={10}
								>
									<ImporterField name="growerName" label="Grower from Form" />
									<ImporterField name="growerEmail" label="Grower Email" optional />
									<ImporterField name="growerCell" label="Grower Cell Phone Number" optional />
									<ImporterField name="fieldID" label="Field ID" optional />
									<ImporterField name="fieldName" label="Field from Form" />
									<ImporterField name="farmName" label="Farm from Form" optional />
									<ImporterField name="syncCode" label="Event ID" optional />
									<ImporterField name="locationFromForm" label="Location from Form" optional />
									<ImporterField name="depth" label="Depth from Form" optional />
									<ImporterField name="labName" label="Lab from Form" optional />
									<ImporterField name="gridSize" label="Grid from Form" optional />
									<ImporterField name="testPackage" label="3rd Party Lab Test Package ID" optional />
									<ImporterField name="ready" label="Field Ready at Submission?" optional />
									<ImporterField name="fieldPriority" label="Priority OVRD" optional />
									<ImporterField name="submissionNotes" label="Submission Notes" optional />
									<ImporterField name="labAccountNumber" label="Lab Account Number" optional />
									<ImporterField name="freqOfMicros" label="Frequency of Micros" optional />
									<ImporterField name="submitterName" label="Submitter" optional />
									<ImporterField name="submitterEmail" label="Submitter Email" optional />
									<ImporterField name="pointsAttached" label="Retest? (Pts Submitted by Customer)" optional />
									<ImporterField name="boundaryChange" label="Boundary Change Type" optional />
									<ImporterField name="jobFlags" label="Job Flags" optional />
									<ImporterField name="specialLabInstructions" label="Lab Instructions" optional />
									<ImporterField name="nextCrop" label="Next Crop" optional />
									<ImporterField name="lastCrop" label="Last Crop" optional />
									<ImporterField name="specsFromForm" label="Specs from Form" optional />
									<ImporterField name="regularLabSubmissionCode" label="Regular Lab Submission Code" optional />
								</Importer>
							)}

							{vm.csvImportType === "ROGO" && <CSVDropzone />}
						</Grid>
						{vm.jobData.length === 0 && (
							<Grid sx={{ width: "100%" }}>
								<Grid container spacing={1} justifyContent="center">
									<Grid>
										<Button
											variant="outlined"
											target="_blank"
											href="https://docs.google.com/spreadsheets/d/1O5G7LBH_g67CSwDsDtZugKsIYYOzEIjUnOVzqgPQ6OQ/edit?usp=sharing"
										>
											<Launch className="icon-in-button" />
											Open Template (Google Sheets)
										</Button>
									</Grid>
									<Grid>
										<Button variant="outlined" onClick={vm.downloadBulkUploadTemplate}>
											<Download className="icon-in-button" />
											Download Template (CSV)
										</Button>
									</Grid>
								</Grid>
							</Grid>
						)}
						{vm.jobData.length > 0 && (
							<>
								<Button
									style={{ width: "100%", marginTop: 10, marginLeft: 8, marginBottom: 10 }}
									variant="contained"
									onClick={vm.resetCSVState}
								>
									<NotInterested className="icon-in-button" />
									Remove CSV file(s)
								</Button>
								<FileTaggingAndSplitting
									keyedShapefileMap={vm.keyedShapefileMap}
									setKeyedShapefileMap={vm.setKeyedShapefileMap}
									selectableFiles={vm.selectableFiles}
									setSelectableFiles={vm.setSelectableFiles}
									originalShapefiles={vm.originalShapefiles}
									setOriginalShapefiles={vm.setOriginalShapefiles}
									loading={vm.loading}
									setLoading={vm.setLoading}
								/>
							</>
						)}
					</>
				)}
			</Grid>
			{vm.jobData.length > 0 && (
				<ImportTableCSV
					jobs={vm.jobData}
					updateJobs={(jobs) => {
						vm.setJobData(jobs);
					}}
					deals={vm.internalSelectedDeal ? [vm.internalSelectedDeal] : []}
					allDeals={props.deals}
					setLoading={(task: string | boolean) => {
						vm.setLoading(!!task);
					}}
					allOptions={flattenArrayObjects(Object.values(vm.selectableFiles))}
					resetForm={() => {
						vm.setJobData([]);
						vm.setOriginalShapefiles([]);
						vm.setSelectableFiles({});
					}}
					user={vm.userContext}
					shouldShowAutoMatch={true}
					setDrawerState={function (open: boolean): void {
						throw new Error("Function not implemented.");
					}}
				/>
			)}
		</>
	);
}
